import React, { useContext, useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { area } from "@turf/turf";
import { toast } from "react-toastify";
import { transformDataToMapboxSource } from "utils";
import { UserContext } from "context/user";
import { MapActions, MapContext } from "context/map";
import { createField } from "api/Farm";
import { updateField } from "api/Field";
import InputField from "components/Input";
import Button from "components/Button";
import "./index.scss";

const ManageFieldDrawer = ({
  previousField,
  setPreviousField,
  saveSwitchField,
  setSaveSwitchField,
}) => {
  const { token, profile } = useContext(UserContext);
  const { state, dispatch } = useContext(MapContext);
  const {
    map,
    farm,
    draw,
    currentField,
    drawerOpen,
    fields,
    cropTypes,
    manageStatus,
    selectedFields,
  } = state;
  const [fieldName, setFieldName] = useState(`Field ${fields.length + 1}`);
  const [cropType, setCropType] = useState("Select Crop Type");
  const [hectare, setHectare] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [cropTypeError, setCropTypeError] = useState("");

  const setFarmDetails = useCallback(() => {
    if (!selectedFields.length) return;

    if (previousField && previousField !== selectedFields[0]) return;

    const feature = fields.find((item) => item.id === selectedFields[0]);

    if (feature) {
      setFieldName(feature.properties.field_name);
      setHectare(feature.properties.area);

      if (cropTypes.includes(feature?.properties?.field_crop_type)) {
        setCropType(feature?.properties?.field_crop_type);
      }
    }
  }, [cropTypes, fields, previousField, selectedFields]);

  useEffect(() => {
    if (!drawerOpen) {
      setFieldName(`Field ${fields.length + 1}`);
      setCropType("Select Crop Type");
    }
  }, [drawerOpen, fields.length]);

  useEffect(() => {
    if (!currentField) return;

    if (drawerOpen) {
      const fieldFeature = draw.get(currentField);

      if (fieldFeature) {
        const calculatedHectare =
          Math.round((area(fieldFeature) / 1000) * 10) / 100;

        setHectare(calculatedHectare);
      }
    }
  }, [currentField, draw, drawerOpen]);

  useEffect(() => {
    if (drawerOpen) {
      setFarmDetails();
    }
  }, [drawerOpen, setFarmDetails]);

  useEffect(() => {
    if (!map) return;

    if (drawerOpen) {
      map.on("draw.update", () => {
        const fieldId = currentField || selectedFields[0];
        const fieldFeature = draw.get(fieldId);

        if (fieldFeature) {
          const calculatedHectare =
            Math.round((area(fieldFeature) / 1000) * 10) / 100;

          setHectare(calculatedHectare);
        }
      });
    }
  }, [currentField, draw, drawerOpen, map, selectedFields]);

  const drawField = useCallback(
    (newFields) => {
      const transformedFields = transformDataToMapboxSource(newFields);

      transformedFields.forEach((transformedField) => {
        const { id } = transformedField.data;

        if (!map.getSource(`source-${id}`)) {
          map.addSource(`source-${id}`, transformedField);
        }

        if (!map.getLayer(`outline-${id}`)) {
          map.addLayer({
            id: `outline-${id}`,
            type: "line",
            source: `source-${id}`,
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
            paint: {
              "line-color": [
                "case",
                ["boolean", ["feature-state", "active"], false],
                "#09BA51",
                "#FFFFFF",
              ],
              "line-width": [
                "case",
                ["boolean", ["feature-state", "active"], false],
                2,
                3,
              ],
              "line-opacity": 0.7,
            },
          });
        }

        if (!map.getLayer(`fill-${id}`)) {
          map.addLayer({
            id: `fill-${id}`,
            type: "fill",
            source: `source-${id}`,
            paint: {
              "fill-color": [
                "case",
                ["boolean", ["feature-state", "active"], false],
                "#09BA51",
                "#FFFFFF",
              ],
              "fill-opacity": [
                "case",
                ["boolean", ["feature-state", "tiled"], false],
                0,
                ["boolean", ["feature-state", "active"], false],
                0.2,
                ["boolean", ["feature-state", "hover"], false],
                0.5,
                0,
              ],
            },
          });
        }
        if (manageStatus === "create-boundaries" || manageStatus === "create") {
          map.on("mousemove", `fill-${id}`, (e) => {
            if (!e.features.length) return;

            map.setFeatureState(
              { source: `source-${id}`, id },
              { hover: true }
            );
          });
          map.on("mouseleave", `fill-${id}`, () => {
            map.setFeatureState(
              { source: `source-${id}`, id },
              { hover: false }
            );
          });
          map.on("click", `fill-${id}`, () => {
            const multiSelectEnabled = profile.feature_flags.find(
              (item) =>
                item.feature_flag === "field:multi_select" &&
                item.enabled === true
            );
            dispatch({
              type: MapActions.SET_SELECTED_FIELDS,
              value: { fieldId: id, multiSelectEnabled },
            });
          });
        }
      });
    },
    [dispatch, manageStatus, map, profile.feature_flags]
  );

  const saveNewField = useCallback(async () => {
    if (cropType === "Select Crop Type" || cropType === "") {
      setCropTypeError(
        "We need you to provide us the crop type for your field."
      );
    } else {
      try {
        draw.setFeatureProperty(currentField, "field_name", fieldName);
        draw.setFeatureProperty(currentField, "field_crop_type", cropType);

        const fieldFeature = draw.get(currentField);
        const params = {
          features: [fieldFeature],
          type: "FeatureCollection",
        };

        const newField = await createField(farm.id, params, token);

        draw.delete(currentField);

        drawField(newField.features);

        dispatch({
          type: MapActions.SAVE_NEW_FIELD,
          value: { field: newField.features[0], switch: saveSwitchField },
        });

        toast.success("Field created successfully!");
      } catch {
        toast.error("Can't create a field!");
      }
    }
    setSubmitting(false);
  }, [
    cropType,
    currentField,
    dispatch,
    draw,
    drawField,
    farm.id,
    fieldName,
    saveSwitchField,
    token,
  ]);

  const saveEditField = useCallback(async () => {
    const id = saveSwitchField ? previousField : selectedFields[0];

    if (cropTypeError || cropType === "Select Crop Type") {
      setCropTypeError(
        "We need you to provide us the crop type for your field"
      );
    } else {
      try {
        draw.setFeatureProperty(id, "field_name", fieldName);
        draw.setFeatureProperty(id, "field_crop_type", cropType);

        const fieldFeature = draw.get(id);

        const updatedField = await updateField(id, fieldFeature, token);

        draw.delete(id);

        dispatch({
          type: MapActions.SAVE_EDIT_FIELD,
          value: { field: updatedField, switch: saveSwitchField },
        });

        drawField([updatedField]);

        toast.success("Field updated successfully!");
      } catch {
        toast.error("Can't update field!");
      }
    }
    setSubmitting(false);
  }, [
    cropType,
    dispatch,
    draw,
    drawField,
    fieldName,
    previousField,
    saveSwitchField,
    selectedFields,
    token,
    cropTypeError,
  ]);

  const handleSave = useCallback(async () => {
    setSubmitting(true);

    if (manageStatus === "create-boundaries" || manageStatus === "create") {
      saveNewField();
    }

    if (manageStatus === "edit") {
      saveEditField();
    }
  }, [manageStatus, saveEditField, saveNewField]);

  const cancelNewField = () => {
    dispatch({ type: MapActions.CANCEL_CREATE_FIELD });

    draw.delete(currentField);
    draw.changeMode("static");
  };

  const cancelEditField = () => {
    draw.delete(selectedFields[0]);

    const filteredFields = fields.filter(
      (item) => item.id === selectedFields[0]
    );

    drawField(filteredFields);

    dispatch({ type: MapActions.CANCEL_EDIT_FIELD });
  };

  const handleCancel = () => {
    if (manageStatus === "create") {
      setCropTypeError("");
      cancelNewField();
    }

    if (manageStatus === "edit") {
      setCropTypeError("");
      cancelEditField();
    }
  };

  useEffect(() => {
    if (saveSwitchField) {
      handleSave();

      setPreviousField(null);
      setSaveSwitchField(false);
    }
  }, [handleSave, saveSwitchField, setPreviousField, setSaveSwitchField]);

  const renderButtons = () => {
    return (
      <>
        <Button
          id="save-button"
          type="solid"
          color="green"
          label="Save Field"
          block
          onClick={() => {
            handleSave();
          }}
          disabled={
            submitting || cropType === "Select Crop Type" || cropTypeError
          }
          loading={submitting}
        />
        <Button
          id="cancel-button"
          type="outlined"
          color="white"
          label="Cancel"
          block
          onClick={() => {
            handleCancel();
          }}
          disabled={submitting}
        />
      </>
    );
  };

  const validateCropType = (e) => {
    const { value } = e.target;

    if (value === "") {
      setCropTypeError(
        "We need you to provide us the crop type for your field"
      );
    } else {
      setCropTypeError("");
    }
  };

  const handleSelectCropType = (event) => {
    validateCropType(event);
    setCropType(event.target.value);
  };

  const renderCropTypes = () => {
    return cropTypes?.map((crop) => (
      <option key={crop} value={crop}>
        {crop}
      </option>
    ));
  };

  return (
    <div className="manage-field">
      <div className="manage-field-wrapper">
        <h3 className="title">Field Details</h3>
        {drawerOpen && (
          <>
            <InputField
              label="Field Name"
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
            />
            <div className="input-field">
              {cropTypeError && (
                <label htmlFor="cropType" className="label -error">
                  {cropTypeError}
                </label>
              )}
              <select
                className={`input ${cropTypeError ? "-error" : ""}`}
                value={cropType}
                onChange={(e) => handleSelectCropType(e)}
              >
                <option value="" selected>
                  Select Crop Type
                </option>
                {renderCropTypes()}
              </select>
              <label className="label">Crop Type</label>
            </div>
            <InputField
              label="Field Area"
              value={`Approx. ${hectare} Ha`}
              disabled
            />
            {renderButtons()}
          </>
        )}
      </div>
    </div>
  );
};

ManageFieldDrawer.defaultProps = {
  previousField: null,
};

ManageFieldDrawer.propTypes = {
  previousField: PropTypes.string,
  setPreviousField: PropTypes.func.isRequired,
  saveSwitchField: PropTypes.bool.isRequired,
  setSaveSwitchField: PropTypes.func.isRequired,
};

export default ManageFieldDrawer;
